import React, { useState, useEffect } from 'react';
import {
  Card,
  Tabs,
  Button,
  Space,
  message,
  Spin,
  Row,
  Col,
  Breadcrumb,
  Typography,
  Tag,
  Divider,
  Alert,
  Progress
} from 'antd';
import {
  SaveOutlined,
  ArrowLeftOutlined,
  EditOutlined,
  UserOutlined,
  SettingOutlined,
  LockOutlined,
  HomeOutlined,
  CheckOutlined
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';

import { UserService, userUtils } from '../../../services/userService';
import type { User, UserCreateRequest, UserUpdateRequest } from '../../../types/user';

import BasicInfoTab from './BasicInfoTab';
import PermissionTab from './PermissionTab';
import SecurityTab from './SecurityTab';

interface UserFormProps {
  mode: 'create' | 'edit' | 'view';
}

interface UserFormState {
  user: User | null;
  loading: boolean;
  saving: boolean;
  activeTab: string;
  hasChanges: boolean;
  validationErrors: Record<string, string[]>;
  completionStatus: {
    basic: boolean;
    position: boolean;
    permission: boolean;
    security: boolean;
  };
}

const { Title, Text } = Typography;

const UserForm: React.FC<UserFormProps> = ({ mode }) => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  
  const [state, setState] = useState<UserFormState>({
    user: null,
    loading: false,
    saving: false,
    activeTab: 'basic',
    hasChanges: false,
    validationErrors: {},
    completionStatus: {
      basic: false,
      position: false,
      permission: false,
      security: false,
    },
  });

  // Load initial data
  useEffect(() => {
    if (mode !== 'create' && userId) {
      loadUser(userId);
    } else {
      // Initialize with default data for create mode
      setState(prev => ({
        ...prev,
        user: {
          ...userUtils.getDefaultUserFormData(),
          userId: '',
          userName: '',
          createdAt: '',
          updatedAt: '',
        } as User,
      }));
    }
  }, [mode, userId]);

  // Load user data
  const loadUser = async (userIdParam: string) => {
    setState(prev => ({ ...prev, loading: true }));
    
    try {
      const user = await UserService.getUser(userIdParam);
      setState(prev => ({
        ...prev,
        user,
        loading: false,
      }));
    } catch (error) {
      message.error(`載入使用者資訊失敗: ${error instanceof Error ? error.message : '未知錯誤'}`);
      setState(prev => ({ ...prev, loading: false }));
    }
  };

  // Handle form data change
  const handleDataChange = (updatedData: Partial<User>) => {
    setState(prev => ({
      ...prev,
      user: prev.user ? { ...prev.user, ...updatedData } : null,
      hasChanges: true,
    }));
  };

  // Validate form data
  const validateForm = (): boolean => {
    if (!state.user) return false;

    const errors: Record<string, string[]> = {};

    // Basic validation
    if (!state.user.userId?.trim()) {
      errors.userId = ['使用者ID為必填項目'];
    } else if (!/^[a-zA-Z0-9_]{3,20}$/.test(state.user.userId)) {
      errors.userId = ['使用者ID必須為3-20位英數字或底線'];
    }

    if (!state.user.userName?.trim()) {
      errors.userName = ['使用者姓名為必填項目'];
    }

    if (state.user.email && !userUtils.validateEmail(state.user.email)) {
      errors.email = ['請輸入有效的電子郵件地址'];
    }

    if (mode === 'create') {
      if (!state.user.dptNo?.trim()) {
        errors.dptNo = ['部門編號為必填項目'];
      }
      if (!state.user.positionId || state.user.positionId < 1) {
        errors.positionId = ['請選擇職位（ID 必須大於 0）'];
      }
    }

    setState(prev => ({ ...prev, validationErrors: errors }));
    return Object.keys(errors).length === 0;
  };

  // Handle save
  const handleSave = async () => {
    if (!state.user || !validateForm()) {
      message.error('請檢查表單資料');
      return;
    }

    setState(prev => ({ ...prev, saving: true }));

    try {
      if (mode === 'create') {
        const createData: UserCreateRequest = {
          userId: state.user.userId,
          userName: state.user.userName,
          email: state.user.email,
          dptNo: state.user.dptNo || '',
          positionId: state.user.positionId || 1,
          isMain: true,
          password: 'Temp123456', // Default password
        };

        const newUser = await UserService.createUser(createData);
        message.success('使用者建立成功');
        navigate(`/user/edit/${newUser.userId}`);
      } else {
        const updateData: UserUpdateRequest = {
          userName: state.user.userName,
          email: state.user.email,
          dptNo: state.user.dptNo,
          isActive: state.user.isActive,
        };

        await UserService.updateUser(state.user.userId, updateData);
        message.success('使用者資訊更新成功');
        setState(prev => ({ ...prev, hasChanges: false }));
      }
    } catch (error) {
      message.error(`儲存失敗: ${error instanceof Error ? error.message : '未知錯誤'}`);
    } finally {
      setState(prev => ({ ...prev, saving: false }));
    }
  };

  // Handle back
  const handleBack = () => {
    if (state.hasChanges) {
      message.warning('您有未儲存的變更，請先儲存或捨棄變更');
      return;
    }
    navigate('/user');
  };

  // Calculate completion progress
  const completionCount = Object.values(state.completionStatus).filter(Boolean).length;
  const completionPercentage = (completionCount / 4) * 100;

  // Get page title
  const getPageTitle = () => {
    switch (mode) {
      case 'create':
        return '新增使用者';
      case 'edit':
        return '編輯使用者';
      case 'view':
        return '檢視使用者';
      default:
        return '使用者資訊';
    }
  };

  // Get status tag
  const getStatusTag = () => {
    if (!state.user) return null;
    
    const statusDisplay = userUtils.getStatusDisplay(state.user.isActive);
    return (
      <Tag color={statusDisplay.color} style={{ marginLeft: '8px' }}>
        {statusDisplay.text}
      </Tag>
    );
  };

  // Tab items configuration
  const tabItems = [
    {
      key: 'basic',
      label: (
        <Space>
          <UserOutlined />
          <span>基本資訊</span>
          {state.completionStatus.basic && <CheckOutlined style={{ color: '#52c41a' }} />}
        </Space>
      ),
      children: (
        <BasicInfoTab
          user={state.user}
          mode={mode}
          onChange={handleDataChange}
          errors={state.validationErrors}
        />
      ),
    },
    {
      key: 'permission',
      label: (
        <Space>
          <SettingOutlined />
          <span>權限設定</span>
          {state.completionStatus.permission && <CheckOutlined style={{ color: '#52c41a' }} />}
        </Space>
      ),
      children: (
        <PermissionTab
          user={state.user}
          mode={mode}
          onChange={handleDataChange}
        />
      ),
    },
    {
      key: 'security',
      label: (
        <Space>
          <LockOutlined />
          <span>安全設定</span>
          {state.completionStatus.security && <CheckOutlined style={{ color: '#52c41a' }} />}
        </Space>
      ),
      children: (
        <SecurityTab
          user={state.user}
          mode={mode}
          onChange={handleDataChange}
        />
      ),
    },
  ];

  if (state.loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '60vh' 
      }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      {/* Breadcrumb */}
      <Breadcrumb style={{ marginBottom: '16px' }}>
        <Breadcrumb.Item>
          <HomeOutlined />
        </Breadcrumb.Item>
        <Breadcrumb.Item>
          <a onClick={() => navigate('/user')}>使用者管理</a>
        </Breadcrumb.Item>
        <Breadcrumb.Item>{getPageTitle()}</Breadcrumb.Item>
      </Breadcrumb>

      {/* Header Card */}
      <Card 
        style={{ 
          marginBottom: '24px',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}
      >
        <Row justify="space-between" align="middle">
          <Col>
            <Space align="center">
              <Button 
                icon={<ArrowLeftOutlined />} 
                onClick={handleBack}
                type="text"
                size="large"
              />
              <div>
                <Title level={3} style={{ margin: 0 }}>
                  {getPageTitle()}
                  {getStatusTag()}
                </Title>
                {state.user && (
                  <Text type="secondary">
                    {mode !== 'create' && `使用者ID: ${UserService.formatUserId(state.user.userId)}`}
                    {mode !== 'create' && state.user.userName && ` • ${state.user.userName}`}
                  </Text>
                )}
              </div>
            </Space>
          </Col>
          <Col>
            {mode === 'create' && (
              <Progress 
                percent={completionPercentage} 
                size="small" 
                style={{ width: '200px', marginRight: '16px' }}
                format={(percent) => `完成度 ${Math.round(percent || 0)}%`}
              />
            )}
            <Space>
              {mode === 'view' && (
                <Button 
                  type="primary"
                  icon={<EditOutlined />}
                  size="large"
                  onClick={() => navigate(`/user/edit/${userId}`)}
                >
                  編輯
                </Button>
              )}
              {mode !== 'view' && (
                <>
                  <Button 
                    size="large"
                    onClick={handleBack}
                  >
                    取消
                  </Button>
                  <Button 
                    type="primary"
                    icon={<SaveOutlined />}
                    size="large"
                    loading={state.saving}
                    onClick={handleSave}
                    disabled={!state.hasChanges && mode === 'edit'}
                  >
                    {mode === 'create' ? '建立使用者' : '儲存變更'}
                  </Button>
                </>
              )}
            </Space>
          </Col>
        </Row>
        
        {/* Show validation errors */}
        {Object.keys(state.validationErrors).length > 0 && (
          <Alert
            style={{ marginTop: '16px' }}
            message="表單驗證錯誤"
            description="請檢查並修正以下錯誤項目"
            type="error"
            showIcon
            closable
          />
        )}
        
        {/* Show unsaved changes warning */}
        {state.hasChanges && (
          <Alert
            style={{ marginTop: '16px' }}
            message="有未儲存的變更"
            description="您已修改了表單內容，請記得儲存變更"
            type="warning"
            showIcon
          />
        )}
      </Card>

      {/* Main Form Card */}
      <Card 
        style={{ 
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}
      >
        <Tabs
          activeKey={state.activeTab}
          onChange={(key) => setState(prev => ({ ...prev, activeTab: key }))}
          items={tabItems}
          size="large"
          tabBarStyle={{
            marginBottom: '24px',
            borderBottom: '2px solid #f0f0f0'
          }}
        />
      </Card>
    </div>
  );
};

export default UserForm;